import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import '../Style/Productindustries.css';
import { useLanguage } from '../Context/Languagecontext';

// ── Asset Asset Manifest Alignment ──
import img1 from '../Assets/lungi.webp';
import img2 from '../Assets/weaving.webp';
import img3 from '../Assets/fabric.webp';
import img4 from '../Assets/distribution.webp';

// Keep structural layout configuration records decoupled from UI text values
const SLIDES_STRUCTURE = [
  { id: 's1', img: img1 },
  { id: 's2', img: img2 },
  { id: 's3', img: img3 },
  { id: 's4', img: img4 },
];

const AUTO_PLAY_MS = 6000;

function HeadingWithAccent({ text, accent }) {
  if (!accent || !text || !text.includes(accent)) return <>{text}</>;
  const parts = text.split(accent);
  return (
    <>
      {parts[0]}
      <em>{accent}</em>
      {parts[1]}
    </>
  );
}

export default function ProductsIndustries() {
  const [current, setCurrent] = useState(0);
  const [exitDir, setExitDir] = useState(null);
  const [progress, setProgress] = useState(0);

  const { t, lang } = useLanguage();
  const moduleData = t.productsIndustries;

  const currentRef   = useRef(0);
  const totalRef     = useRef(SLIDES_STRUCTURE.length);
  const progressRef  = useRef(null);
  const startTimeRef = useRef(null);
  const sectionRef   = useRef(null);

  /* ── Join language dictionary content with structural assets layout mapping ── */
  const localizedSlides = useMemo(() => {
    const rawSlidesCopy = moduleData?.slides || {};
    return SLIDES_STRUCTURE.map((slide) => {
      const copy = rawSlidesCopy[slide.id] || {};
      return {
        ...slide,
        heading: copy.heading || '',
        headingAccent: copy.headingAccent || '',
        tagline: copy.tagline || '',
        body: copy.body || '',
      };
    });
  }, [moduleData]);

  /* ── Keep ref in sync with state ── */
  useEffect(() => {
    currentRef.current = current;
  }, [current]);

  /* ── Progress bar tracking timeline configuration logic ── */
  const startProgress = useCallback(() => {
    cancelAnimationFrame(progressRef.current);
    setProgress(0);
    startTimeRef.current = performance.now();

    const tick = (now) => {
      const elapsed = now - startTimeRef.current;
      const pct = Math.min((elapsed / AUTO_PLAY_MS) * 100, 100);
      setProgress(pct);
      if (pct < 100) progressRef.current = requestAnimationFrame(tick);
    };
    progressRef.current = requestAnimationFrame(tick);
  }, []);

  /* ── Advance to next slide ── */
  const advance = useCallback(() => {
    const next = (currentRef.current + 1) % totalRef.current;
    setExitDir('left');
    setTimeout(() => {
      setCurrent(next);
      setExitDir(null);
    }, 50);
    startProgress();
  }, [startProgress]);

  /* ── Auto-play lifecycle configuration logic ── */
  const advanceRef = useRef(advance);
  useEffect(() => { 
    advanceRef.current = advance; 
  }, [advance]);

  useEffect(() => {
    startProgress();
    const id = setInterval(() => advanceRef.current(), AUTO_PLAY_MS);
    return () => {
      clearInterval(id);
      cancelAnimationFrame(progressRef.current);
    };
  }, [startProgress]);

  /* ── Scroll intersection view target setup ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { 
        if (entry.isIntersecting) {
          entry.target.classList.add('pi-reveal');
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const total = localizedSlides.length;

  return (
    <section 
      ref={sectionRef} 
      className={`pi-section ${lang === 'ta' ? 'pi-section--tamil' : ''}`} 
      aria-label={moduleData?.headerTitle || "Products & Industries"}
    >
      {/* Fixed top header strip */}
      <div className="pi-header-strip">
        <p className="pi-title">{moduleData?.headerTitle}</p>
        <p className="pi-subtitle">{moduleData?.headerSubtitle}</p>
      </div>

      {/* Slides View Frame */}
      <div className="pi-slides">
        {localizedSlides.map((slide, i) => {
          let cls = 'pi-slide';
          if (i === current) cls += ' active';
          else if (exitDir === 'left'  && i === (current - 1 + total) % total) cls += ' exit-left';
          else if (exitDir === 'right' && i === (current + 1) % total)        cls += ' exit-right';

          return (
            <div key={slide.id} className={cls} aria-hidden={i !== current}>
              <img
                className="pi-bg"
                src={slide.img}
                alt={slide.heading}
                loading={i === 0 ? 'eager' : 'lazy'}
              />
              <div className="pi-overlay" />
              <span className="pi-slide-num" aria-hidden="true">0{i + 1}</span>
              <div className="pi-content">
                <p className="pi-section-label">{moduleData?.sectionLabel}</p>
                <h2 className="pi-heading">
                  <HeadingWithAccent text={slide.heading} accent={slide.headingAccent} />
                </h2>
                <p className="pi-tagline">{slide.tagline}</p>
                <div className="pi-divider" aria-hidden="true" />
                <p className="pi-body">{slide.body}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dot Indicators Panel */}
      <div className="pi-dots" role="tablist" aria-label="Slide indicators">
        {localizedSlides.map((_, i) => (
          <button
            key={i}
            className={`pi-dot ${i === current ? 'active' : ''}`}
            onClick={() => {
              if (i === current) return;
              setExitDir(i > current ? 'left' : 'right');
              setTimeout(() => { 
                setCurrent(i); 
                setExitDir(null); 
              }, 50);
              startProgress();
            }}
            role="tab"
            aria-selected={i === current}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Slide Index Counter */}
      <div className="pi-counter" aria-live="polite">
        <span>{String(current + 1).padStart(2, '0')}</span> / {String(total).padStart(2, '0')}
      </div>

      {/* Synchronized Linear Active Progress Rail */}
      <div
        className="pi-progress"
        style={{ width: `${progress}%` }}
        role="progressbar"
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
      />
    </section>
  );
}